import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, Download, CheckCircle2, AlertCircle, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const UpdateModal = ({
  isOpen,
  onClose,
  state = {},
  onCheck,
  onDownload,
}) => {
  const { t } = useTranslation('shared');
  const {
    status = 'idle',
    info = null,
    error = null,
    currentVersion = null,
    progress = null,
    lastCheckedAt = null,
  } = state;

  // Hook'ları her zaman en üstte çağırmalıyız (React Hooks Rules)
  const lastCheckedText = useMemo(() => {
    if (!lastCheckedAt) return null;
    try {
      return new Date(lastCheckedAt).toLocaleString();
    } catch (_) {
      return lastCheckedAt;
    }
  }, [lastCheckedAt]);

  // Early return hook'lardan sonra olmalı
  if (!isOpen) return null;

  const isChecking = status === 'checking';
  const isDownloading = status === 'downloading';
  const isDownloaded = status === 'downloaded';
  const isInstalling = status === 'installing';
  const hasAvailableUpdate = ['available', 'downloading', 'downloaded', 'installing'].includes(status);
  const percent = Math.min(
    100,
    Math.max(0, Math.round(progress?.percent ?? (isDownloaded || isInstalling ? 100 : 0)))
  );
  const canTriggerDownload = status === 'available';
  const downloadButtonLabel = (() => {
    if (isDownloading) {
      return t('updateModal.downloadingPercent', { percent });
    }
    if (isDownloaded || isInstalling) {
      return t('updateModal.installStarting');
    }
    return t('updateModal.updateNow');
  })();
  const isDownloadButtonDisabled = !canTriggerDownload || isChecking || isInstalling || isDownloaded;
  const downloadIconClasses = `${isDownloading ? 'animate-pulse ' : ''}w-4 h-4`;
  const availabilityTitle = (() => {
    if (status === 'available') return t('updateModal.availability.readyTitle');
    if (isDownloading) return t('updateModal.availability.downloadingTitle');
    if (isDownloaded) return t('updateModal.availability.downloadedTitle');
    if (isInstalling) return t('updateModal.availability.installingTitle');
    return t('updateModal.availability.infoTitle');
  })();
  const availabilityDescription = (() => {
    if (status === 'available') {
      return t('updateModal.availability.readyDescription');
    }
    if (isDownloading) {
      return t('updateModal.availability.downloadingDescription');
    }
    if (isDownloaded) {
      return t('updateModal.availability.downloadedDescription');
    }
    if (isInstalling) {
      return t('updateModal.availability.installingDescription');
    }
    return null;
  })();
  const StatusIcon = isDownloading || isInstalling ? RefreshCw : CheckCircle2;
  const statusIconClasses =
    (isDownloading || isInstalling ? 'animate-spin ' : '') + 'w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0';
  const releaseNotesContent = info?.releaseNotes ?? '';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100000] flex items-center justify-center px-4 bg-black/70 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ scale: 0.94, opacity: 0, y: -20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: -20 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            className="relative bg-[var(--color-bg-base)] w-full max-w-2xl rounded-2xl shadow-2xl border border-white/10 dark:border-white/10 overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)] transition-colors"
              aria-label={t('updateModal.close')}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-6 pt-6 pb-4 border-b border-white/5 dark:border-white/5">
              <h2 className="text-2xl font-bold text-white">{t('updateModal.title')}</h2>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {t(`updateModal.statusTexts.${status}`, { defaultValue: t('updateModal.statusTexts.idle') })}
              </p>
              {lastCheckedText && (
                <p className="text-xs text-[var(--color-text-muted)] mt-2">
                  {t('updateModal.lastChecked', { time: lastCheckedText })}
                </p>
              )}
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/5 dark:border-white/5 bg-[var(--color-bg-elevated)]/30 p-4">
                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">{t('updateModal.currentVersion')}</p>
                  <p className="text-lg font-semibold text-white mt-1">
                    v{currentVersion || '—'}
                  </p>
                </div>

                <div className="rounded-xl border border-blue-500/20 dark:border-blue-500/30 bg-blue-500/5 dark:bg-blue-500/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-blue-400">{t('updateModal.latestVersion')}</p>
                  <p className="text-lg font-semibold text-blue-400 dark:text-blue-300 mt-1">
                    {info?.version ? `v${info.version}` : t('updateModal.waitingForCheck')}
                  </p>
                </div>
              </div>

              {hasAvailableUpdate && (
                <div className="flex items-start gap-3 rounded-xl border border-green-500/20 dark:border-green-500/30 bg-green-500/5 dark:bg-green-500/10 p-4">
                  <StatusIcon className={statusIconClasses} />
                  <div>
                    <p className="font-semibold text-green-200">{availabilityTitle}</p>
                    {availabilityDescription && (
                      <p className="text-sm text-green-300/80 mt-1">{availabilityDescription}</p>
                    )}
                    {info?.releaseDate && (
                      <p className="text-xs text-green-400/60 mt-2">
                        {t('updateModal.releaseDate', { date: new Date(info.releaseDate).toLocaleDateString() })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {status === 'error' && error && (
                <div className="flex items-start gap-3 rounded-xl border border-red-500/20 dark:border-red-500/40 bg-red-500/5 dark:bg-red-500/10 p-4">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-200">{t('updateModal.updateErrorTitle')}</p>
                    <p className="text-sm text-red-300/80 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {isDownloading && (
                <DownloadProgress percent={percent} progress={progress} />
              )}

              {isInstalling && (
                <div className="rounded-xl border border-indigo-500/20 dark:border-indigo-500/40 bg-indigo-500/5 dark:bg-indigo-500/10 p-4">
                  <p className="text-sm font-semibold text-indigo-200 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('updateModal.installStartingLong')}
                  </p>
                  <p className="text-xs text-indigo-300/60 mt-2">
                    {t('updateModal.installingHint')}
                  </p>
                </div>
              )}

              <section>
                <h3 className="text-sm font-semibold text-white mb-3">{t('updateModal.releaseNotesHeading')}</h3>
                <ReleaseNotes content={releaseNotesContent} />
              </section>
            </div>

            <div className="px-6 py-5 border-t border-white/5 dark:border-white/5 bg-[var(--color-bg-base)]/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-[var(--color-text-muted)]">
                {t('updateModal.footerHint')}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={onCheck}
                  disabled={isChecking || isInstalling}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] transition-colors disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
                  {t('updateModal.checkForUpdates')}
                </button>

                 <button
                  onClick={onDownload}
                  disabled={isDownloadButtonDisabled}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold transition-colors shadow-sm disabled:opacity-60"
                >
                  <Download className={downloadIconClasses} />
                  {downloadButtonLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
};

const formatSpeed = (bytesPerSecond) => {
  if (!bytesPerSecond || bytesPerSecond <= 0) return null;
  return `${formatBytes(bytesPerSecond)}/s`;
};

const DownloadProgress = ({ percent, progress }) => {
  const { t } = useTranslation('shared');
  const safePercent = Math.min(100, Math.max(0, percent || 0));
  const speed = formatSpeed(progress?.bytesPerSecond);
  const transferred = progress?.transferred ? formatBytes(progress.transferred) : null;
  const total = progress?.total ? formatBytes(progress.total) : null;

  return (
    <div className="rounded-xl border border-blue-500/20 dark:border-blue-500/30 bg-blue-500/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-blue-400 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin flex-shrink-0" />
          {t('updateModal.downloadingLabel')}
        </p>
        <span className="text-sm font-bold text-blue-400 tabular-nums">
          {safePercent}%
        </span>
      </div>

      {/* İlerleme çubuğu */}
      <div className="h-3 w-full rounded-full bg-blue-900/40 overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-blue-600 to-blue-400"
          initial={{ width: 0 }}
          animate={{ width: `${safePercent}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
      </div>

      {/* Alt bilgiler */}
      <div className="flex items-center justify-between mt-2 text-xs text-blue-400/60">
        <span className="tabular-nums">
          {transferred && total ? `${transferred} / ${total}` : t('updateModal.downloadedPercent', { percent: safePercent })}
        </span>
        {speed && (
          <span className="flex items-center gap-1 tabular-nums">
            <Zap className="w-3 h-3" />
            {speed}
          </span>
        )}
      </div>
    </div>
  );
};

const ReleaseNotes = ({ content }) => {
  const { t } = useTranslation('shared');
  if (!content || !content.trim()) {
    return (
      <div className="rounded-xl border border-dashed border-white/10 p-5 text-sm text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]/20">
        {t('updateModal.noReleaseNotes')}
      </div>
    );
  }

  const trimmedContent = content.trim();
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(trimmedContent);

  if (isHtml) {
    return (
      <div
        className="rounded-xl border border-white/5 bg-[var(--color-bg-elevated)]/40 p-5 text-sm leading-relaxed text-[var(--color-text-secondary)] shadow-sm"
        dangerouslySetInnerHTML={{ __html: trimmedContent }}
      />
    );
  }

  return (
    <pre className="bg-[var(--color-bg-elevated)]/40 border border-white/5 rounded-xl p-5 text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap leading-relaxed shadow-sm">
      {trimmedContent}
    </pre>
  );
};


export default UpdateModal;